import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Divider } from 'antd';
import {
  categoryMetaAtom,
  categoryMetaKeysAtom,
  categoryMetaOrderAtom,
  flowDataAtom,
} from '../atoms';
import CategorySection from './CategorySection';
import { StyledTitle } from '../styled';

const MetricSelectorRightPanel = ({
  searchText,
  selectedMetricList,
  lockedMetricsSet,
  onChange,
  onBulkChange,
  setRefFn,
  metricCategories,
  shouldShowFlow,
}) => {
  const productCategoryKeys = useAtomValue(categoryMetaKeysAtom);
  const productCategoryMeta = useAtomValue(categoryMetaAtom);
  const productCategoryOrder = useAtomValue(categoryMetaOrderAtom);

  const { flowMetricCategoryOrder, flowMetricCategoryMeta, flowConfigMeta } =
    useAtomValue(flowDataAtom);

  // Filter metrics by search text and build list of filtered category keys
  const { filteredMetricCategories, filteredProductCategoryOrder } = useMemo(() => {
    if (!searchText) {
      return {
        filteredMetricCategories: metricCategories,
        filteredProductCategoryOrder: productCategoryOrder,
      };
    }

    const lowerSearch = searchText.toLowerCase();
    const filtered: Record<string, any> = {};
    const filteredCategories: string[] = [];

    productCategoryOrder.forEach((categoryKey) => {
      const categoryMetricKey = productCategoryKeys[categoryKey];
      const categoryMetrics = metricCategories[categoryMetricKey];
      if (!categoryMetrics) return;

      const matchingMetrics: Record<string, any> = {};

      Object.entries(categoryMetrics).forEach(([metricKey, metric]: [string, any]) => {
        const nameMatch = metric.name?.toLowerCase().includes(lowerSearch);
        const displayNameMatch = metric.displayName?.toLowerCase().includes(lowerSearch);

        if (nameMatch || displayNameMatch) {
          matchingMetrics[metricKey] = metric;
        }
      });

      if (Object.keys(matchingMetrics).length > 0) {
        filtered[categoryMetricKey] = matchingMetrics;
        filteredCategories.push(categoryKey);
      }
    });

    return {
      filteredMetricCategories: filtered,
      filteredProductCategoryOrder: filteredCategories,
    };
  }, [metricCategories, searchText, productCategoryOrder, productCategoryKeys]);

  // Filter flows by search text and build list of filtered flow category keys
  const { filteredFlowCategories, filteredFlowCategoryOrder } = useMemo(() => {
    if (!searchText || !flowConfigMeta) {
      return {
        filteredFlowCategories: flowConfigMeta,
        filteredFlowCategoryOrder: flowMetricCategoryOrder,
      };
    }

    const lowerSearch = searchText.toLowerCase();
    const filtered: Record<string, any> = {};
    const filteredCategories: string[] = [];

    flowMetricCategoryOrder.forEach((categoryKey) => {
      const categoryFlows = flowConfigMeta[categoryKey];
      if (!categoryFlows) return;

      const matchingFlows: Record<string, any> = {};

      Object.entries(categoryFlows).forEach(([flowKey, flow]: [string, any]) => {
        const nameMatch = flow.name?.toLowerCase().includes(lowerSearch);
        const displayNameMatch = flow.displayName?.toLowerCase().includes(lowerSearch);

        if (nameMatch || displayNameMatch) {
          matchingFlows[flowKey] = flow;
        }
      });

      if (Object.keys(matchingFlows).length > 0) {
        filtered[categoryKey] = matchingFlows;
        filteredCategories.push(categoryKey);
      }
    });

    return {
      filteredFlowCategories: filtered,
      filteredFlowCategoryOrder: filteredCategories,
    };
  }, [flowConfigMeta, searchText, flowMetricCategoryOrder]);

  const hasVisibleMetrics = filteredProductCategoryOrder.length > 0;
  const hasVisibleFlows = shouldShowFlow && filteredFlowCategoryOrder.length > 0;

  return (
    <>
      {/* Metrics Section */}
      {hasVisibleMetrics && (
        <>
          <h3>Metrics</h3>
          {filteredProductCategoryOrder.map((categoryKey) => {
            const categoryMetricKey = productCategoryKeys[categoryKey];
            const metrics = Object.values(filteredMetricCategories[categoryMetricKey]);
            const categoryMeta = productCategoryMeta[categoryKey];

            return (
              <CategorySection
                key={categoryKey}
                categoryKey={categoryKey}
                metrics={metrics}
                setRefFn={setRefFn}
                categoryMeta={categoryMeta}
                selectedMetricList={selectedMetricList}
                lockedMetricsSet={lockedMetricsSet}
                onChange={onChange}
                onBulkChange={onBulkChange}
                isFlow={false}
                searchText={searchText}
              />
            );
          })}
        </>
      )}

      {/* Flows Section */}
      {shouldShowFlow && (
        <>
          {hasVisibleMetrics && <Divider />}
          {hasVisibleFlows && (
            <>
              <StyledTitle>
                <h3>Flows</h3>
              </StyledTitle>
              {filteredFlowCategoryOrder.map((categoryKey) => {
                const metrics = Object.values(filteredFlowCategories[categoryKey]);
                const categoryMeta = flowMetricCategoryMeta[categoryKey];
                return (
                  <CategorySection
                    key={categoryKey}
                    categoryKey={categoryKey}
                    metrics={metrics}
                    setRefFn={setRefFn}
                    categoryMeta={categoryMeta}
                    selectedMetricList={selectedMetricList}
                    lockedMetricsSet={lockedMetricsSet}
                    onChange={onChange}
                    onBulkChange={onBulkChange}
                    isFlow={true}
                    searchText={searchText}
                  />
                );
              })}
            </>
          )}
        </>
      )}

      {/* Show message when nothing matches search */}
      {!hasVisibleMetrics && !hasVisibleFlows && searchText && (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#999' }}>
          No results found for &ldquo;{searchText}&rdquo;
        </div>
      )}
    </>
  );
};

export default MetricSelectorRightPanel;
